import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { FormField } from '@/types/form';
import { FieldPreview } from './FieldPreview';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { 
  GripVertical, 
  Edit2, 
  Copy, 
  Trash2, 
  Eye,
  EyeOff 
} from 'lucide-react';
import { cn } from '@/lib/utils';

interface SortableFieldItemProps {
  field: FormField;
  isSelected: boolean;
  isDragging: boolean;
  onClick: () => void;
  onUpdate: (updates: Partial<FormField>) => void;
  onDelete: () => void;
  onDuplicate: () => void;
}

export const SortableFieldItem: React.FC<SortableFieldItemProps> = ({
  field,
  isSelected,
  isDragging,
  onClick,
  onUpdate,
  onDelete,
  onDuplicate,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging: isSortableDragging,
  } = useSortable({ id: field.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const handleToggleRequired = (e: React.MouseEvent) => {
    e.stopPropagation();
    onUpdate({ required: !field.required });
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (window.confirm('Are you sure you want to delete this field?')) {
      onDelete();
    }
  };

  const handleDuplicate = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDuplicate();
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'group relative',
        isSortableDragging && 'z-50',
        isDragging && 'opacity-50'
      )}
    >
      <Card 
        className={cn(
          'cursor-pointer transition-all duration-200 hover:shadow-md',
          isSelected && 'ring-2 ring-primary ring-offset-2',
          isSortableDragging && 'shadow-lg rotate-2'
        )}
        onClick={onClick}
      >
        <CardContent className="p-4">
          {/* Field Header */}
          <div className="flex items-start gap-3 mb-3">
            {/* Drag Handle */}
            <div
              className="cursor-grab active:cursor-grabbing opacity-0 group-hover:opacity-100 transition-opacity"
              {...attributes}
              {...listeners}
            >
              <GripVertical className="h-4 w-4 text-muted-foreground" />
            </div>

            {/* Field Info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="font-medium text-sm truncate">{field.label}</h3>
                {field.required && (
                  <span className="text-red-500 text-xs">*</span>
                )}
                <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded">
                  {field.type}
                </span>
              </div>
              <p className="text-xs text-muted-foreground truncate">
                {field.name} • {field.description || 'No description'}
              </p>
            </div>

            {/* Field Actions */}
            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={handleToggleRequired}
                title={field.required ? 'Make optional' : 'Make required'}
              >
                {field.required ? (
                  <Eye className="h-3 w-3" />
                ) : (
                  <EyeOff className="h-3 w-3" />
                )}
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={onClick}
                title="Edit field"
              >
                <Edit2 className="h-3 w-3" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={handleDuplicate}
                title="Duplicate field"
              >
                <Copy className="h-3 w-3" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-red-600 hover:text-red-700"
                onClick={handleDelete}
                title="Delete field"
              >
                <Trash2 className="h-3 w-3" />
              </Button>
            </div>
          </div>

          {/* Field Preview */}
          <div className="pl-7">
            <FieldPreview field={field} />
          </div>
        </CardContent>
      </Card>

      {/* Selection Indicator */}
      {isSelected && (
        <div className="absolute -left-2 top-1/2 transform -translate-y-1/2 w-1 h-8 bg-primary rounded-full" />
      )}
    </div>
  );
};